/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

export enum DIR {
	LTR = "ltr",
	RTL = "rtl",
}

export type Direction = "ltr" | "rtl";

/**
 * RTL languages, by BCP 47 language subtag (ISO 639-1 and -3).
 *
 * Matched on the subtag rather than the full tag. The previous list held exact
 * strings (`["ar", "ar-ar", "ar-AR"]`), so every regional Arabic tag — `ar-SA`,
 * `ar-AE` — resolved LTR, as did Hebrew, Persian and Urdu entirely.
 *
 * Latent rather than live: the only RTL locale any site currently ships is bare
 * `ar`, which the old list already matched, and `ar-SA` is redirected to `ar` by
 * `commonUnsupportedLocaleMapping`. So this is correctness for the next RTL
 * locale added, not a fix for something users are hitting today.
 *
 * What widening this set does and doesn't change, because it is easy to
 * under-test:
 *
 * - `getDirection` feeds `<html dir>` only. Every layout still hardcodes
 *   `dir="ltr"` on `#cosmos-app`, and `globals.css` has a nested `[dir="ltr"]`
 *   rule that outranks `html[dir="rtl"] *`, so page layout does not flip.
 *   Removing that hardcode is separate work.
 * - `isLangRtl` also backs `useIsRtl` / `useDirection` (`../hooks/direction.hooks`),
 *   and those DO mirror rendered output — `Animate`, `EdsIcon.inner`,
 *   `HeroAlpha.HeroImage` and `LogoTitleDescButtonStack` branch on them for icon
 *   and animation direction. So a newly-matching locale changes those components
 *   even though the page frame stays LTR. Regression-test them when enabling one.
 *
 * Each entry checked against ICU (`new Intl.Locale(x).maximize().getTextInfo()`).
 * `ku` is deliberately absent: it maximizes to `ku-Latn-TR`, which is LTR — RTL
 * Kurdish is `ckb` (below) or `ku-Arab`, and the latter needs script-subtag
 * handling this function doesn't do. Direction comes from the language subtag
 * alone, so a contradicting script subtag is ignored both ways — `ar-Latn` reads
 * RTL and `az-Arab` reads LTR, both against ICU. Tracked on MW-13398.
 *
 * Deliberately a static list rather than calling `getTextInfo()` at runtime:
 * `getDirection` runs server-side computing `<html dir>` while `useDirection`
 * runs in the browser, so an engine-dependent answer could disagree between
 * Node's ICU and the client's.
 */
const RTL_LANGUAGE_SUBTAGS = new Set([
	"ar", // Arabic
	"arc", // Aramaic
	"ckb", // Central Kurdish (Sorani)
	"dv", // Divehi
	"fa", // Persian
	"he", // Hebrew
	"iw", // Hebrew, deprecated ISO 639-1 — still emitted by some clients
	"ji", // Yiddish, deprecated ISO 639-1
	"ks", // Kashmiri
	"nqo", // N'Ko
	"ps", // Pashto
	"sd", // Sindhi
	"syr", // Syriac
	"ug", // Uyghur
	"ur", // Urdu
	"yi", // Yiddish
]);

/**
 * Is this locale written right-to-left?
 *
 * Accepts a full BCP 47 tag or a bare subtag, case-insensitively, and tolerates
 * both `-` and `_` separators since callers pass values straight through from
 * route params and CMS fields.
 *
 * @param locale - e.g. "ar", "ar-SA", "he_IL", "en-US". Widened to accept
 *        null/undefined because callers pass route params straight through.
 */
export const isLangRtl = (locale: string | null | undefined): boolean => {
	if (!locale || typeof locale !== "string") return false;
	// Split on either separator; the language subtag is always first.
	const subtag = locale.trim().toLowerCase().split(/[-_]/)[0];
	return RTL_LANGUAGE_SUBTAGS.has(subtag);
};

export const getDirection = (locale: string | null | undefined): Direction => {
	const isRtl = isLangRtl(locale);
	return isRtl ? "rtl" : "ltr";
};
